import type { EquipmentRarity } from './catalog';
import type { EquipmentRoll } from './roll';
import { componentContribution, type EquipmentTotals } from './totals';

/**
 * Power: one number that says which of two components is better.
 *
 * ## What it is not
 *
 * It never reaches combat and never reaches a reward. Nothing in the
 * simulation, the ledger, the drop tables or the market fees reads it. It is a
 * comparison, and if this whole file were deleted the game would play
 * identically - which is the property that lets it be tuned without touching
 * an economy.
 *
 * ## Why it cannot be a sum
 *
 * The nine attributes are measured in five different units. +1 attack per
 * second, +1% damage and +1 Gold per enemy have nothing in common, and adding
 * their raw numbers would rank a Receptor above a Núcleo purely because Gold
 * happens to be counted in smaller pieces. So Power is not a sum of the item -
 * it is the GAIN the item produces, measured by putting it on a player.
 *
 * ## The reference profiles
 *
 * Five fixed players, standing in for roughly maps 1, 5, 10, 15 and 20. They
 * belong to the VERSION, never to the account asking: the same instance is
 * worth the same Power to everybody, which is what makes the number tradeable
 * information rather than a personalised estimate.
 *
 * For each profile the item is evaluated three times:
 *
 * - **DPS**, at 60%. Damage per projectile times attacks per second, so
 *   attack and attack speed COMPOUND exactly as they do in the arena - an item
 *   carrying both is worth more than the two halves scored separately, and this
 *   is where the bonus attack correctly earns less on a profile with a large
 *   percentage stack than a percentage would.
 * - **Effective health**, at 25%.
 * - **Gold per enemy**, at 15%.
 *
 * The kill-rate half of "Gold per minute" is deliberately NOT counted here: it
 * is already paid for in the DPS term, and counting it again would let one
 * attribute buy two thirds of the score.
 *
 * ## Additive percentages, and what they mean for the ranking
 *
 * Percentages in KENOCYBER are additive with each other, so a fixed +5% is
 * worth relatively less to a player who already has +250%. The advanced
 * profiles therefore rank percentage attributes below flat ones on the same
 * axis - that is a true property of the system rather than a modelling choice,
 * and averaging over five profiles is what stops the score describing only a
 * beginner or only an end-game player.
 */

/** Bumped whenever the profiles, the weights or the arithmetic below change. */
export const POWER_VERSION = 1;

/** Gains are carried as basis points of relative gain: 10 000 = +100%. */
const GAIN_SCALE = 10_000n;
const BPS = 10_000n;

/** Weights, in whole percent. They sum to 100 and are proved to by a test. */
export const POWER_WEIGHTS = Object.freeze({
  offence: 60n,
  survival: 25n,
  economy: 15n,
});

/**
 * One reference player, in the same integer units the attributes use.
 *
 * PROVISIONAL, and part of `POWER_VERSION`: these are what a player looks like
 * at roughly that map, extrapolated from the numbers map 1 actually runs on
 * (`HUNT_BALANCE`: 1 damage, 1 attack/s, 100 health, half a Gold per mob) plus
 * the points, ship bonus, artifacts and equipment such a player would carry.
 * The twenty maps do not exist yet; these profiles are what "map 15" means to
 * this calculation and to nothing else.
 */
export interface PowerProfile {
  readonly mapIndex: number;
  readonly baseAttackMilli: bigint;
  readonly attackBps: bigint;
  readonly baseAttackSpeedMicro: bigint;
  readonly attackSpeedBps: bigint;
  readonly maxHealthMilli: bigint;
  readonly maxHealthBps: bigint;
  readonly goldPerEnemyMilli: bigint;
  readonly goldBps: bigint;
}

export const POWER_PROFILES: readonly PowerProfile[] = Object.freeze([
  Object.freeze({
    mapIndex: 1,
    baseAttackMilli: 1_000n,
    attackBps: 200n,
    baseAttackSpeedMicro: 1_000_000n,
    attackSpeedBps: 0n,
    maxHealthMilli: 100_000n,
    maxHealthBps: 0n,
    goldPerEnemyMilli: 500n,
    goldBps: 0n,
  }),
  Object.freeze({
    mapIndex: 5,
    baseAttackMilli: 8_000n,
    attackBps: 1_600n,
    baseAttackSpeedMicro: 1_600_000n,
    attackSpeedBps: 200n,
    maxHealthMilli: 260_000n,
    maxHealthBps: 600n,
    goldPerEnemyMilli: 1_200n,
    goldBps: 400n,
  }),
  Object.freeze({
    mapIndex: 10,
    baseAttackMilli: 40_000n,
    attackBps: 5_000n,
    baseAttackSpeedMicro: 3_000_000n,
    attackSpeedBps: 800n,
    maxHealthMilli: 700_000n,
    maxHealthBps: 2_000n,
    goldPerEnemyMilli: 3_000n,
    goldBps: 1_200n,
  }),
  Object.freeze({
    mapIndex: 15,
    baseAttackMilli: 150_000n,
    attackBps: 12_000n,
    baseAttackSpeedMicro: 6_000_000n,
    attackSpeedBps: 2_000n,
    maxHealthMilli: 1_800_000n,
    maxHealthBps: 5_000n,
    goldPerEnemyMilli: 7_500n,
    goldBps: 2_500n,
  }),
  Object.freeze({
    mapIndex: 20,
    baseAttackMilli: 500_000n,
    attackBps: 25_000n,
    baseAttackSpeedMicro: 12_000_000n,
    attackSpeedBps: 4_000n,
    maxHealthMilli: 4_500_000n,
    maxHealthBps: 10_000n,
    goldPerEnemyMilli: 18_000n,
    goldBps: 5_000n,
  }),
]);

/** `after / before - 1`, in basis points, exact until one final division. */
function relativeGainBps(before: bigint, after: bigint): bigint {
  if (before <= 0n) {
    return 0n;
  }
  return (after * GAIN_SCALE) / before - GAIN_SCALE;
}

/** What the three axes are worth on ONE profile. Exposed for the report. */
export interface ProfileGain {
  readonly mapIndex: number;
  readonly offenceBps: bigint;
  readonly survivalBps: bigint;
  readonly economyBps: bigint;
}

export function gainOnProfile(profile: PowerProfile, totals: EquipmentTotals): ProfileGain {
  // Damage per projectile: the bonus attack lands OUTSIDE the multiplier,
  // exactly as it does in `hunt/passive-attributes.ts`.
  const damageBefore = profile.baseAttackMilli * (BPS + profile.attackBps);
  const damageAfter =
    (profile.baseAttackMilli + totals.baseAttackMilli) *
      (BPS + profile.attackBps + totals.attackBps) +
    totals.bonusAttackMilli * BPS;

  const speedBefore = profile.baseAttackSpeedMicro * (BPS + profile.attackSpeedBps);
  const speedAfter =
    (profile.baseAttackSpeedMicro + totals.baseAttackSpeedMicro) *
    (BPS + profile.attackSpeedBps + totals.attackSpeedBps);

  // The synergy is in the product: an item carrying attack AND attack speed
  // scores more than the sum of the two evaluated apart, because that is what
  // it actually does to the arena.
  const health = {
    before: profile.maxHealthMilli * (BPS + profile.maxHealthBps),
    after:
      (profile.maxHealthMilli + totals.maxHealthMilli) *
      (BPS + profile.maxHealthBps + totals.maxHealthBps),
  };
  const gold = {
    before: profile.goldPerEnemyMilli * (BPS + profile.goldBps),
    after:
      (profile.goldPerEnemyMilli + totals.goldPerEnemyMilli) *
      (BPS + profile.goldBps + totals.goldBps),
  };

  return {
    mapIndex: profile.mapIndex,
    offenceBps: relativeGainBps(damageBefore * speedBefore, damageAfter * speedAfter),
    survivalBps: relativeGainBps(health.before, health.after),
    economyBps: relativeGainBps(gold.before, gold.after),
  };
}

/** Every profile's verdict, for the interface and for the balance report. */
export function powerBreakdown(totals: EquipmentTotals): readonly ProfileGain[] {
  return POWER_PROFILES.map((profile) => gainOnProfile(profile, totals));
}

/**
 * The Power of a set of totals: the weighted mean gain across every profile.
 *
 * Always a positive integer. An item whose gain rounds below one still scores
 * 1: a component that exists is worth more than nothing, and a zero would make
 * two very different Uncommons compare equal.
 */
export function powerOfTotals(totals: EquipmentTotals): number {
  let weighted = 0n;
  for (const profile of POWER_PROFILES) {
    const gain = gainOnProfile(profile, totals);
    weighted +=
      POWER_WEIGHTS.offence * gain.offenceBps +
      POWER_WEIGHTS.survival * gain.survivalBps +
      POWER_WEIGHTS.economy * gain.economyBps;
  }
  // One division at the end: the weights sum to 100 and there are N profiles.
  const power = weighted / (100n * BigInt(POWER_PROFILES.length));
  return power < 1n ? 1 : Number(power);
}

/**
 * The Power of ONE instance at ONE level.
 *
 * Depends on the roll and the level and on nothing else - not the owner, not
 * their map, not their points, not what else they have equipped. Two accounts
 * holding the same instance see the same number, which is what a marketplace
 * needs it to mean.
 */
export function powerOfInstance(roll: EquipmentRoll, level: number): number {
  return powerOfTotals(
    componentContribution({
      instanceId: '',
      definitionId: '',
      component: roll.component,
      level,
      roll,
    }),
  );
}

/**
 * What one attribute alone is worth at one level, for the ranking in the docs
 * and for the balance test.
 *
 * The rarity is irrelevant here and is not taken: a lone attribute is a lone
 * attribute. It exists so "which of the nine is strongest" is a computed answer
 * rather than an opinion.
 */
export function powerOfSingleAttribute(
  roll: EquipmentRoll,
  level: number,
  _rarity?: EquipmentRarity,
): number {
  return powerOfInstance(roll, level);
}
